import {
	CACHE_TIERS,
	type Festival,
	type FestivalDetails,
	type ProcessedSet,
} from "@workspace/types";
import { useMemo } from "react";
import { trpc } from "@/lib/trpc/react";

// Types
// Festival type is imported from @workspace/types
// Extended Festival type with user-specific data
export interface UserFestival extends Festival {
	status?: string;
	user_festival_rating?: number;
	user_sets_seen?: number;
	user_reviews_count?: number;
	user_want_to_see_count?: number;
	userHasReviewed?: boolean;
}

export interface UserFestivalsData {
	interestedFestivals: UserFestival[];
	goingFestivals: UserFestival[];
	pastFestivals: UserFestival[];
	allFestivals: UserFestival[];
}

export interface FestivalPageData {
	festival: FestivalDetails | null;
	sets: ProcessedSet[];
	userProfile: any;
	setReviews: any[];
	festivalReview: any;
}

// Query keys for consistent cache management
export const festivalDataKeys = {
	all: ["festival-data"] as const,
	detail: (slug: string) => [...festivalDataKeys.all, "detail", slug] as const,
	sets: (festivalId: string) =>
		[...festivalDataKeys.all, "sets", festivalId] as const,
	userFestival: (userId: string, festivalId: string) =>
		[...festivalDataKeys.all, "user-festival", userId, festivalId] as const,
	list: ["festivals-list"] as const,
	userFestivals: (userId: string) =>
		[...festivalDataKeys.all, "user-festivals", userId] as const,
	search: (searchTerm: string) =>
		[...festivalDataKeys.all, "search", searchTerm] as const,
};

// Enhanced search function that matches names, locations, dates, and aliases
const US_STATES_MAP = {
	alabama: "AL",
	alaska: "AK",
	arizona: "AZ",
	arkansas: "AR",
	california: "CA",
	colorado: "CO",
	connecticut: "CT",
	delaware: "DE",
	florida: "FL",
	georgia: "GA",
	hawaii: "HI",
	idaho: "ID",
	illinois: "IL",
	indiana: "IN",
	iowa: "IA",
	kansas: "KS",
	kentucky: "KY",
	louisiana: "LA",
	maine: "ME",
	maryland: "MD",
	massachusetts: "MA",
	michigan: "MI",
	minnesota: "MN",
	mississippi: "MS",
	missouri: "MO",
	montana: "MT",
	nebraska: "NE",
	nevada: "NV",
	"new hampshire": "NH",
	"new jersey": "NJ",
	"new mexico": "NM",
	"new york": "NY",
	"north carolina": "NC",
	"north dakota": "ND",
	ohio: "OH",
	oklahoma: "OK",
	oregon: "OR",
	pennsylvania: "PA",
	"rhode island": "RI",
	"south carolina": "SC",
	"south dakota": "SD",
	tennessee: "TN",
	texas: "TX",
	utah: "UT",
	vermont: "VT",
	virginia: "VA",
	washington: "WA",
	"west virginia": "WV",
	wisconsin: "WI",
	wyoming: "WY",
} as const;

function _festivalMatchesSearch(
	festival: Festival | UserFestival,
	searchTerm: string,
): boolean {
	if (!searchTerm.trim()) return true;

	const search = searchTerm.toLowerCase().trim();

	// Name search (partial matching)
	if (festival.name?.toLowerCase().includes(search)) return true;

	// Alias search (e.g., searching "Electric Daisy Carnival" finds "EDC" festivals)
	if (festival.alias?.toLowerCase().includes(search)) return true;

	return false;
}

/**
 * Hook for festival details and stages using tRPC
 */
export function useFestivalDetails(
	festivalSlug: string,
): ReturnType<typeof trpc.festival.getBySlug.useQuery> {
	return trpc.festival.getBySlug.useQuery(
		{ slug: festivalSlug },
		{
			...CACHE_TIERS.STATIC, // Static content - festival details rarely change
			retry: (failureCount, error) => {
				// Don't retry if festival not found
				if (error && typeof error === "object" && "data" in error) {
					const data = error.data as any;
					if (data?.code === "NOT_FOUND") return false;
				}
				return failureCount < 2;
			},
		},
	);
}

/**
 * Hook for festival sets using tRPC
 */
export function useFestivalSets(
	festivalId: string | null,
	eventType: "main_festival" | "pre_party" | "after_party" = "main_festival",
): ReturnType<typeof trpc.festival.getSets.useQuery> {
	return trpc.festival.getSets.useQuery(
		{ festivalId: festivalId!, eventType },
		{
			enabled: !!festivalId,
			...CACHE_TIERS.STATIC, // Set data + review aggregations (facepiles fetched separately)
		},
	);
}

/**
 * Hook for user festival data using tRPC
 */
export function useUserFestivalData(
	userId: string,
	festivalId: string | null,
	eventType: "main_festival" | "pre_party" | "after_party" = "main_festival",
): ReturnType<typeof trpc.festival.getUserFestivalData.useQuery> {
	return trpc.festival.getUserFestivalData.useQuery(
		{ userId, festivalId: festivalId!, eventType },
		{
			enabled: !!festivalId,
			...CACHE_TIERS.REALTIME, // Real-time for user-specific data
			retry: (failureCount, error) => {
				// Don't retry schema/critical errors
				if (error && typeof error === "object" && "message" in error) {
					const errorMessage = error.message as string;
					if (
						errorMessage.includes("Schema") ||
						errorMessage.includes("critical")
					) {
						return false;
					}
				}
				return failureCount < 2;
			},
		},
	);
}

/**
 * Hook for all festivals list using tRPC
 */
export function useFestivalsList(): ReturnType<
	typeof trpc.festival.list.useQuery
> {
	return trpc.festival.list.useQuery(
		{
			tab: "all",
			limit: 50,
		},
		{
			...CACHE_TIERS.STATIC, // Festival list data
			select: (data) => data?.items || [],
		},
	);
}

/**
 * Hook for user's festivals (attending, interested, past) using tRPC
 */
export function useUserFestivalsProfile(userId: string | null | undefined) {
	const allFestivalsQuery = trpc.user.getProfileFestivals.useQuery(
		{
			userId: userId!,
			tab: "all",
			limit: 50,
		},
		{
			enabled: !!userId,
			...CACHE_TIERS.DYNAMIC, // Profile festival lists can be cached
		},
	);

	const upcomingQuery = trpc.user.getProfileFestivals.useQuery(
		{
			userId: userId!,
			tab: "upcoming",
			limit: 50,
		},
		{
			enabled: !!userId,
			...CACHE_TIERS.DYNAMIC, // Profile festival lists can be cached
		},
	);

	const pastQuery = trpc.user.getProfileFestivals.useQuery(
		{
			userId: userId!,
			tab: "past",
			limit: 50,
		},
		{
			enabled: !!userId,
			...CACHE_TIERS.DYNAMIC, // Profile festival lists can be cached
		},
	);

	return useMemo(() => {
		if (!allFestivalsQuery.data || !upcomingQuery.data || !pastQuery.data) {
			return {
				data: {
					interestedFestivals: [],
					goingFestivals: [],
					pastFestivals: [],
					allFestivals: [],
				},
				isLoading:
					allFestivalsQuery.isLoading ||
					upcomingQuery.isLoading ||
					pastQuery.isLoading,
				error:
					allFestivalsQuery.error || upcomingQuery.error || pastQuery.error,
			};
		}

		const allFestivals = allFestivalsQuery.data.items || [];
		const upcomingFestivals = upcomingQuery.data.items || [];
		const pastFestivals = pastQuery.data.items || [];

		// Filter upcoming festivals by status
		const interestedFestivals = upcomingFestivals.filter(
			(f: UserFestival) => f.status === "interested",
		);
		const goingFestivals = upcomingFestivals.filter(
			(f: UserFestival) =>
				f.status === "going" ||
				f.user_sets_seen ||
				f.user_reviews_count ||
				f.user_want_to_see_count,
		);

		return {
			data: {
				interestedFestivals,
				goingFestivals,
				pastFestivals,
				allFestivals,
			},
			isLoading: false,
			error: null,
		};
	}, [allFestivalsQuery, upcomingQuery, pastQuery]);
}

/**
 * Combined hook with search filtering using tRPC
 */
export function useFestivalsWithSearch(
	searchTerm: string,
	_userId: string | null,
) {
	const festivalsQuery = trpc.festival.list.useQuery({
		search: searchTerm,
		tab: "all",
		limit: 50,
	});

	const processedFestivals = useMemo(() => {
		if (!festivalsQuery.data) return [];
		return (festivalsQuery.data as any)?.items || [];
	}, [festivalsQuery.data]);

	return {
		festivals: processedFestivals,
		isLoading: festivalsQuery.isLoading,
		error: festivalsQuery.error,
		isError: festivalsQuery.isError,
	};
}

/**
 * Combined hook for all festival page data using tRPC
 */
export function useFestivalPageData(
	userId: string,
	festivalSlug: string,
): {
	festival: ReturnType<typeof useFestivalDetails>;
	sets: ReturnType<typeof useFestivalSets>;
	user: ReturnType<typeof useUserFestivalData>;
	isLoading: boolean;
	hasError: boolean;
	error: any;
} {
	const festivalQuery = useFestivalDetails(festivalSlug);

	// Use the festival ID from the first query for dependent queries
	const typedData = festivalQuery.data as
		| { festival?: { id?: string } }
		| undefined;
	const festivalId = typedData?.festival?.id;

	const setsQuery = useFestivalSets(festivalId || null);
	const userQuery = useUserFestivalData(userId, festivalId || null);

	return {
		festival: festivalQuery,
		sets: setsQuery,
		user: userQuery,
		isLoading:
			festivalQuery.isLoading || setsQuery.isLoading || userQuery.isLoading,
		hasError: festivalQuery.isError || setsQuery.isError || userQuery.isError,
		error: festivalQuery.error || setsQuery.error || userQuery.error,
	};
}
